import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Eye, Package, FileText, MessageSquare, TrendingUp, Calendar } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['hsl(338, 60%, 45%)', 'hsl(173, 58%, 39%)', 'hsl(36, 50%, 55%)', 'hsl(200, 50%, 50%)', 'hsl(280, 40%, 55%)'];

export default function AdminDashboard() {
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    axios.get(`${API}/admin/analytics`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setAnalytics(r.data)).catch(() => {});
  }, []);

  if (!analytics) return <div className="text-center py-16 text-muted-foreground">Loading analytics...</div>;

  const stats = [
    { icon: Eye, label: 'Total Visits', value: analytics.total_visits, sub: `${analytics.today_visits} today`, color: 'bg-primary/10 text-primary' },
    { icon: TrendingUp, label: 'This Week', value: analytics.week_visits, sub: `${analytics.month_visits} this month`, color: 'bg-blue-50 text-blue-600' },
    { icon: Package, label: 'Products', value: analytics.total_products, sub: 'Total products', color: 'bg-amber-50 text-amber-600' },
    { icon: FileText, label: 'Blog Posts', value: analytics.total_blogs, sub: 'Published', color: 'bg-emerald-50 text-emerald-600' },
    { icon: MessageSquare, label: 'Messages', value: analytics.total_contacts, sub: 'Contact submissions', color: 'bg-purple-50 text-purple-600' },
    { icon: Calendar, label: 'Today', value: analytics.today_visits, sub: 'Page views', color: 'bg-rose-50 text-rose-600' },
  ];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-heading text-2xl font-medium mb-8">Dashboard</h1>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {stats.map((s, i) => (
          <Card key={i} className="rounded-2xl border-border" data-testid={`stat-card-${i}`}>
            <CardContent className="p-4">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${s.color}`}>
                <s.icon className="w-5 h-5" />
              </div>
              <p className="text-2xl font-heading font-bold">{s.value}</p>
              <p className="text-sm font-medium mt-0.5">{s.label}</p>
              <p className="text-xs text-muted-foreground">{s.sub}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Daily Visits Chart */}
        <Card className="rounded-2xl border-border lg:col-span-2" data-testid="daily-visits-chart">
          <CardContent className="p-6">
            <h3 className="font-heading text-lg font-medium mb-4">Daily Visits (Last 7 Days)</h3>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={analytics.daily_visits}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(105, 14%, 86%)" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="hsl(135, 8%, 45%)" />
                  <YAxis tick={{ fontSize: 12 }} stroke="hsl(135, 8%, 45%)" />
                  <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid hsl(105, 14%, 86%)' }} />
                  <Bar dataKey="visits" fill="hsl(338, 60%, 45%)" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Page Breakdown */}
        <Card className="rounded-2xl border-border" data-testid="page-breakdown-chart">
          <CardContent className="p-6">
            <h3 className="font-heading text-lg font-medium mb-4">Top Pages</h3>
            {analytics.page_breakdown.length > 0 ? (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={analytics.page_breakdown} dataKey="visits" nameKey="page" cx="50%" cy="50%" outerRadius={100} label={({ page, percent }) => `${page} (${(percent * 100).toFixed(0)}%)`} labelLine={false}>
                      {analytics.page_breakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : <p className="text-muted-foreground text-sm py-8 text-center">No page data yet</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
